/**
 *
 *  Content Scroller
 *  Allows to scroll Listing components
 *
 *  Created by Vladimir Shishlyannikov on 23/02/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import { useState, useEffect, useRef, useCallback } from "react";
import useDebouncedCallback from "../useDebouncedCallback/useDebouncedCallback";

export default function useContentScroller(params) {
  const [currentState, setCurrentState] = useState(generateState(params)),
    currentStateRef = useRef(currentState),
    lastStateRef = useRef(currentState),
    [scrollingContainerRef, setScrollingContainerRef] = useState(null),
    _scrollingContainerRef = useRef(scrollingContainerRef),
    preloadHandlersMapRef = useRef(new Map()), // handler, preloadCount
    onChangeHandlersSetRef = useRef(new Set());

  useEffect(() => {
    const preloaders = preloadHandlersMapRef.current,
      onChangeHandlersSet = onChangeHandlersSetRef.current;

    return () => {
      preloaders.clear();
      onChangeHandlersSet.clear();
    };
  }, []);

  const maybeRunPreloaders = useDebouncedCallback(({ state }) => {
    const previousState = currentStateRef.current;

    setCurrentState(state);

    currentStateRef.current = state;

    const lastVisibleItemsByIndex = previousState.visibleItemsByIndex,
      visibleItemsByIndex = state.visibleItemsByIndex,
      newlyVisibleIndexes = visibleItemsByIndex.filter(
        (val) => !lastVisibleItemsByIndex.includes(val)
      ),
      newlyHiddenIndexes = lastVisibleItemsByIndex.filter(
        (val) => !visibleItemsByIndex.includes(val)
      ),
      numOfVisibleItems = visibleItemsByIndex.length || 1,
      maxVisibleItems = Math.max(
        ...(visibleItemsByIndex.length > 0 ? visibleItemsByIndex : [0])
      );

    function invokeHandlers(index, preloadCount, handler) {
      const allow =
        index <
        maxVisibleItems +
          (numOfVisibleItems + (preloadCount != null ? preloadCount : 3));

      if (
        newlyVisibleIndexes.includes(index) ||
        newlyHiddenIndexes.includes(index) ||
        allow
      ) {
        if (handler != null) {
          handler(state, previousState);
        }
      }
    }

    if ("reset" !== state.reason) {
      preloadHandlersMapRef.current.forEach(
        ({ handler, preloadCount }, key) => {
          /* ! */ invokeHandlers(key, preloadCount, handler);
        }
      );
    }

    return state;
  }, 50);

  const addStateChangeHandler = useCallback((key, handler) => {
    preloadHandlersMapRef.current.set(key, handler);

    return () => {
      preloadHandlersMapRef.current.delete(key);
    };
  }, []);

  const addOperationChangeHandler = useCallback((handler) => {
    onChangeHandlersSetRef.current.add(handler);

    return () => {
      onChangeHandlersSetRef.current.delete(handler);
    };
  }, []);

  const onOperationCb = useCallback(({ reason }) => {
    var lastState = lastStateRef.current;

    const _lastState =
        lastState !== null && lastState !== undefined
          ? lastState
          : generateState(),
      newState = {
        ..._lastState,
        reason: reason,
        timestamp: new Date(),
      };

    lastStateRef.current = newState;

    const onChangeHandlersSet = onChangeHandlersSetRef.current;

    return onChangeHandlersSet.size > 0
      ? setTimeout(
          () =>
            onChangeHandlersSet.forEach((onChangeHandler) =>
              onChangeHandler(reason)
            ),
          {
            priority: "background",
          }
        )
      : Promise.resolve();
  }, []);

  const recalcCb = useCallback(
    ({ reason, key, entry }) => {
      var lastState = lastStateRef.current;

      const _scrollingContainer = _scrollingContainerRef.current;

      if (!_scrollingContainer) {
        throw new Error(
          "scrollingContainer must not be empty before calling reset."
        );
      }

      const _lastState =
          lastState !== null && lastState !== undefined
            ? lastState
            : generateState(),
        {
          childBounds,
          childElementByKey,
          elementStateByIndex,
          reason: _reason,
          timestamp,
          visibleItemsByIndex,
        } = _lastState,
        elementStateByIndexMap = new Map(elementStateByIndex);

      if (key !== undefined) {
        var isIntersecting = entry == null ? undefined : entry.isIntersecting;

        const scrollingContainerChildrenItem =
          _scrollingContainer.children.item(key);

        childElementByKey.set(key, scrollingContainerChildrenItem);

        entry &&
          childBounds.set(
            scrollingContainerChildrenItem,
            entry.boundingClientRect
          );

        elementStateByIndexMap.set(
          key,
          isIntersecting === null ||
            isIntersecting === undefined ||
            isIntersecting
        );
      }

      (reason !== "reset" && reason !== "children_changed") ||
        Array.from(_scrollingContainer.children || []).forEach((t, n) => {
          const l = t;

          childElementByKey.set(n, l);
        });

      const newTimestamp = new Date(),
        newState = {
          childBounds,
          childElementByKey,
          elementStateByIndex: elementStateByIndexMap,
          previousElementStateByIndex: elementStateByIndex,
          previousReason: _reason,
          previousVisibleItemsByIndex: visibleItemsByIndex,
          previousTimestamp: timestamp,
          reason: reason,
          timestamp: newTimestamp,
          visibleItemsByIndex: getVisibleItemsByIndexFn(elementStateByIndexMap),
        };

      lastStateRef.current = newState;

      maybeRunPreloaders({ state: newState });
    },
    [maybeRunPreloaders]
  );

  const [stateManagementApi, setStateManagementApi] = useState({
    addOperationChangeHandler,
    addStateChangeHandler,
    onOperation: onOperationCb,
    recalc: recalcCb,
    state: lastStateRef,
  });

  useEffect(
    function () {
      scrollingContainerRef &&
        setStateManagementApi({
          addOperationChangeHandler,
          addStateChangeHandler,
          onOperation: onOperationCb,
          recalc: recalcCb,
          scrollingContainer: scrollingContainerRef,
          state: lastStateRef,
        });
    },
    [
      addOperationChangeHandler,
      addStateChangeHandler,
      onOperationCb,
      recalcCb,
      scrollingContainerRef,
    ]
  );

  const setScrollingContainerRefCb_O = useCallback((t) => {
    _scrollingContainerRef.current = t;

    setScrollingContainerRef(t);
  }, []);

  return {
    stateManagementApi: stateManagementApi,
    scrollingContainerRef: setScrollingContainerRefCb_O,
  };
}

function generateState(args) {
  const {
    childBounds,
    childElementByKey,
    elementStateByIndex,
    previousElementStateByIndex,
    timestamp,
    ...params
  } = args || {};

  return {
    ...params,
    childBounds: new Map(childBounds != null ? childBounds : []),
    childElementByKey: new Map(
      childElementByKey != null ? childElementByKey : []
    ),
    elementStateByIndex: new Map(
      elementStateByIndex != null ? elementStateByIndex : []
    ),
    previousElementStateByIndex: new Map(
      previousElementStateByIndex != null ? previousElementStateByIndex : []
    ),
    previousVisibleItemsByIndex: [],
    timestamp: timestamp != null ? timestamp : new Date(),
    visibleItemsByIndex: [],
  };
}

function getVisibleItemsByIndexFn(elementStateByIndex) {
  const items = [];

  for (const [key, val] of elementStateByIndex) {
    val && items.push(key);
  }

  return items;
}
